import { useTranslation } from "react-i18next"
import styled from "styled-components"
import ChargerType from "../types/ChargerType"
import Modal from "../components/Modal"
import Icon from "../Icon"

export type ChargerFilter = { [key: string]: string }

// The charger fields users can filter by (everything except the per-charger unique/continuous ones:
// id, serial, name, owner id, IPs, ICCID, SSIDs, timestamps, uptime, MID kWh).
// 'select' fields get a dropdown of the distinct values present in the data; 'bool' fields get
// an Any / Yes / No dropdown (with optional custom yes/no labels).
const ACCESS_MODE_LABELS: { [key: string]: string } = {
  '0': '0 - CAM_HOME_CHG',
  '1': '1 - CAM_HOME_CHG_RFID',
  '2': '2 - CAM_HOME_CHG_RFID_OCPP',
  '3': '3 - CAM_OCPP_CHG_RFID',
  '4': '4 - CAM_OCPP_CHG_FREE',
}

const CHARGER_FILTER_FIELDS: { key: keyof ChargerType, labelKey: string, kind: 'select' | 'bool', yesKey?: string, noKey?: string, valueLabels?: { [key: string]: string } }[] = [
  { key: 'online', labelKey: 'chargers_status', kind: 'bool', yesKey: 'charger_online', noKey: 'charger_offline' },
  { key: 'platform', labelKey: 'charger_platform', kind: 'select' },
  { key: 'storage', labelKey: 'charger_storage', kind: 'select' },
  { key: 'mcuVersion', labelKey: 'charger_mcuVersion', kind: 'select' },
  { key: 'hpowVersion', labelKey: 'charger_hpowVersion', kind: 'select' },
  { key: 'accessMode', labelKey: 'charger_accessMode', kind: 'select', valueLabels: ACCESS_MODE_LABELS },
  { key: 'gsmMode', labelKey: 'charger_gsmMode', kind: 'select' },
  { key: 'gsmModem', labelKey: 'charger_gsmModem', kind: 'select' },
  { key: 'gsmApn', labelKey: 'charger_gsmApn', kind: 'select' },
  { key: 'gsmNetwork', labelKey: 'charger_gsmNetwork', kind: 'select' },
  { key: 'ethMode', labelKey: 'charger_ethernetMode', kind: 'select' },
  { key: 'wifiMode', labelKey: 'charger_wifiMode', kind: 'select' },
  { key: 'dlmMode', labelKey: 'charger_dlmMode', kind: 'select' },
  { key: 'dlmMeter', labelKey: 'charger_dlmMeter', kind: 'select' },
  { key: 'midMeter', labelKey: 'charger_midMeter', kind: 'select' },
  { key: 'midMeterEn', labelKey: 'charger_midMeterEnabled', kind: 'bool' },
  { key: 'psEnabled', labelKey: 'charger_powerSharing', kind: 'bool' },
  { key: 'outOfService', labelKey: 'charger_outOfService', kind: 'bool' },
]

export function chargerMatchesFilter(charger: ChargerType, filter: ChargerFilter): boolean {
  for (const field of CHARGER_FILTER_FIELDS) {
    const selected = filter[field.key as string]
    if (!selected || selected === 'any') continue
    if (field.kind === 'bool') {
      if ((selected === 'yes') !== !!charger[field.key]) return false
    } else {
      if (String(charger[field.key] ?? '') !== selected) return false
    }
  }
  return true
}

export function countActiveChargerFilters(filter: ChargerFilter): number {
  return CHARGER_FILTER_FIELDS.filter(field => filter[field.key as string] && filter[field.key as string] !== 'any').length
}

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const FilterLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_500};
  margin-bottom: 4px;
`

const FilterSelect = styled.select`
  width: 100%;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  background-color: transparent;
  color: ${props => props.theme.colors.gray_900};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
`

const ClearButton = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  margin: 16px 0 0 0;
`

export function ChargerFilterModal({ chargers, filter, onChange, onClose }: { chargers: ChargerType[], filter: ChargerFilter, onChange: (filter: ChargerFilter) => void, onClose: () => void }) {
  const { t } = useTranslation()

  // Distinct, present values for a select field, so the dropdown only offers what actually exists
  const optionsFor = (key: keyof ChargerType) =>
    Array.from(new Set(chargers.map(c => c[key]).filter(v => v != null && v !== '' && v !== ' ').map(String))).sort()

  return (
    <Modal onEscape={onClose}>
      <div style={{ padding: '24px', width: 'min(max(42vw, 538px), calc(100vw - 72px))', position: 'relative' }}>
        <ModalCloseButtonContainer>
          <Icon path="general.x" width="20px" height="20px" onClick={onClose} />
        </ModalCloseButtonContainer>
        <p style={{ fontWeight: 700, fontSize: '16px', margin: 0, marginBottom: '16px' }}>{t('chargers_filter')}</p>
        <div style={{ maxHeight: '65dvh', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '4px' }}>
          {CHARGER_FILTER_FIELDS.map(field => (
            <div key={field.key} style={{ width: 'calc(50% - 6px)', boxSizing: 'border-box' }}>
              <FilterLabel>{t(field.labelKey)}</FilterLabel>
              <FilterSelect value={filter[field.key as string] || 'any'} onChange={e => onChange({ ...filter, [field.key]: e.target.value })}>
                <option value="any">{t('chargers_any')}</option>
                {field.kind === 'bool'
                  ? [<option key="yes" value="yes">{t(field.yesKey || 'charger_yes')}</option>, <option key="no" value="no">{t(field.noKey || 'charger_no')}</option>]
                  : optionsFor(field.key).map(v => <option key={v} value={v}>{field.valueLabels?.[v] ?? v}</option>)}
              </FilterSelect>
            </div>
          ))}
        </div>
        {countActiveChargerFilters(filter) > 0 && <ClearButton onClick={() => onChange({})}>{t('chargers_clearFilters')}</ClearButton>}
      </div>
    </Modal>
  )
}
